/**
 * crucible#3187 — the phone asset-review CONTROLLER (renamed from the old
 * `PhoneSteeringDriver`, extracted out of `TvSteeringSurface.tsx`, to make
 * explicit that the phone shows CONTROLS ONLY — no asset imagery, status, or
 * progress. TV is the display: the asset grid, per-card status, the stage
 * rail, and the focus ring all render there (`TvSteeringSurface.tsx`).
 *
 * The phone drives a "currently focused asset" via swipe/press navigation
 * (see `carousel-focus.ts`) so ANY feedback given always applies unambiguously
 * to whichever asset the TV is currently highlighting — no LLM guessing needed
 * (crucible#3187's `targetId` contract), and no need to look at the phone to
 * know what you're controlling.
 *
 * Layout (top to bottom):
 *   1. "Approve all" — pinned, ALWAYS rendered for an approve-each stage
 *      (never hidden — disabled-with-a-reason during the #2671 solo-gate, so
 *      the top slot stays stable).
 *   2. The gesture pad — focused asset's short name + position dots, swipe or
 *      press `<`/`>` to move focus. No thumbnail, no status chip, no progress
 *      line (the TV owns all of that).
 *   3. The steer composer (mic + textarea + send) — `SteerComposer.tsx`.
 *   4. "Keep it" — a full-width brand-gradient CTA, approves the focused
 *      asset. Mirrors `SoulInterviewTurnCard`'s moodboard "Lock this look"
 *      pattern (same CSS treatment), per Sean's build-time/consumer-surface
 *      visual-unity steer.
 *
 * A pick-one (moodboard) stage degrades to the PRE-#3187 guidance layout
 * (voice/composer only, no pad/dots/approve-all/keep-it) — the carousel
 * doesn't apply to picking ONE look, and per the issue this stage is dead
 * code for new manifests (`reconcileMoodboardAnchor` never emits `pick-one`
 * on a live build); this is a graceful-degrade path for an old snapshot only.
 */
import { useRef, useState } from 'react';
import type { AssetReviewItem } from '@volley/crucible-ipc-types';
import { voiceInputErrorMessage } from '../lib/voice-error.js';
import type { MoodboardCandidate } from './tv-steering-types.js';
import type { CarouselCard, StepDirection } from './carousel-focus.js';
import { SWIPE_THRESHOLD_PX, resolveSwipeStep } from './carousel-focus.js';
import { SteerComposer } from './SteerComposer.js';
import { useSpeechFeedback } from '../hooks/useSpeechFeedback.js';

/** "Approve all" never hides for an approve-each stage — only its enabled
 *  state changes. `hidden` is reserved for the pick-one degrade path, where
 *  batch-approving N candidates isn't a coherent action at all. */
export type ApproveAllState = 'hidden' | 'disabled-gate' | 'enabled';

export interface PhoneSteeringControllerTestIds {
  root: string;
  voiceButton: string;
  voiceError: string;
  guidance: string;
  approveAll: string;
  composer: string;
  composerInput: string;
  composerSend: string;
  pad: string;
  dots: string;
  focusedLabel: string;
  prev: string;
  next: string;
  keepIt: string;
}

export interface PhoneSteeringControllerProps {
  /** Ordered carousel cards for the current (approve-each) stage. Empty
   *  during a pick-one stage — see `pickCandidates` for that path instead. */
  cards: CarouselCard[];
  /** The currently focused card id, or null (nothing to focus yet / pick-one
   *  mode, where the carousel doesn't apply). */
  focusedAssetId: string | null;
  /** Move focus by one step (prev/next press or a completed swipe). */
  onStepFocus: (direction: StepDirection) => void;
  /** Pick-one (moodboard) candidates — present only on the legacy degrade path. */
  pickCandidates: MoodboardCandidate[];
  /** A command targeting the FOCUSED card (or a genuinely stage-wide op) is
   *  in flight — gates the composer/mic/Keep it (server is the sole
   *  authority on what a command does). Does NOT reflect a sibling card
   *  regenerating elsewhere; see `approveAllBusy` for that. */
  busy: boolean;
  /** crucible#3361 — Approve-all is stage-wide (touches EVERY open card), so
   *  it must stay disabled while ANY card is regenerating, not just the
   *  focused one — `busy` alone would re-enable it the moment focus moves
   *  off a still-in-flight sibling. */
  approveAllBusy: boolean;
  /** crucible#2783 — the focused card's approve is sent but not yet confirmed
   *  `approved` in the snapshot (slow under concurrent build-time generation).
   *  Keep it shows "Saving…" and stays disabled so it can't re-fire an approve
   *  that's already on its way. */
  focusedPendingApprove?: boolean;
  feedback: string;
  onFeedbackChange: (value: string) => void;
  onSubmit: () => void;
  /** "Keep it" — approves the focused asset. */
  onKeepIt: () => void;
  approveAllState: ApproveAllState;
  onApproveAll: () => void;
  testIds: PhoneSteeringControllerTestIds;
}

/** Mirrors `TvSteeringSurface`'s private `assetCategoryLabel` +
 *  `assetTvDescriptor` (kept as a small, intentionally-duplicated local copy
 *  rather than a cross-file import — see the module docblock: the phone
 *  controller must not import from `TvSteeringSurface.tsx`, which imports
 *  this file, to avoid a circular module dependency). Both surfaces render
 *  the SAME short name for a given asset; if the TV's naming rule changes,
 *  update this copy alongside it. */
function categoryLabel(category: string): string {
  switch (category) {
    case 'audio':
      return 'Sound';
    case 'background':
      return 'Background';
    case 'portrait':
    case 'sprite':
      return 'Character';
    case 'prop':
      return 'Prop';
    case 'ui':
    case 'shape':
      return 'Interface';
    case 'moodboard':
      return 'Style';
    default:
      return 'Game art';
  }
}

function focusedCardLabel(card: AssetReviewItem): string {
  const titleCased = card.id
    .split(/[\s_-]+/g)
    .filter(Boolean)
    .filter((word) => !['keyframe', 'sprite', 'portrait', 'image', 'asset'].includes(word))
    .map((word) => `${word.slice(0, 1).toUpperCase()}${word.slice(1)}`)
    .join(' ');
  const twoWords = titleCased.split(/\s+/g).filter(Boolean).slice(0, 2).join(' ');
  return twoWords || categoryLabel(card.category);
}

export function PhoneSteeringController({
  cards,
  focusedAssetId,
  onStepFocus,
  pickCandidates,
  busy,
  approveAllBusy,
  focusedPendingApprove = false,
  feedback,
  onFeedbackChange,
  onSubmit,
  onKeepIt,
  approveAllState,
  onApproveAll,
  testIds,
}: PhoneSteeringControllerProps): React.JSX.Element {
  const pickingLook = pickCandidates.length > 0;
  const focusedCard = cards.find((card) => card.id === focusedAssetId) ?? null;
  // crucible#3361 (Bugbot MEDIUM, PR #3353 review) — a still-GENERATING
  // focused card (present but not yet reviewable) used to leave the composer
  // enabled: only Keep it checked reviewability. Typing/speaking feedback then
  // sent that card's `targetId`; the proxy treats a non-reviewable target as
  // absent and falls through to a different asset, so the note silently
  // landed on the wrong piece. The composer and Keep it now share the SAME
  // "is the focused piece actionable right now" check.
  const canCommand = pickingLook || (focusedCard !== null && focusedCard.reviewable);
  const voice = useSpeechFeedback({ active: canCommand, onTranscript: onFeedbackChange });

  const dragStartX = useRef<number | null>(null);

  const placeholder = pickingLook
    ? 'use look A, tweak look B warmer, or redo all'
    : focusedCard
      ? 'keep it, tweak this warmer, or redo all darker'
      : 'waiting for the TV';

  // ── Pick-one (moodboard) graceful degrade — pre-#3187 guidance layout ────
  if (pickingLook) {
    return (
      <section
        className="pointer-events-auto relative flex h-full min-h-0 flex-col overflow-hidden rounded-[38px] border border-white/15 bg-[#07050c]/95 p-3 shadow-2xl shadow-black/55"
        data-testid={testIds.root}
        data-consumer-surface="phone-driver"
        data-input-primitive="voice-first-text-fallback"
        data-phone-driver-ready="true"
        aria-label="Foundry voice guide"
      >
        <div className="min-h-0 flex-1 rounded-[30px] border border-white/10 bg-black/45 p-3 pb-24">
          <div className="flex h-full min-h-0 flex-col justify-center gap-3 overflow-hidden">
            <p className="truncate text-center text-[12px] font-semibold uppercase tracking-[0.2em] text-crucible-accent/85">
              Foundry guide
            </p>
            {/* crucible#3361 (Bugbot LOW, PR #3353 review) — this used to
                ALSO render its own large circular voice button here, wired to
                the same `useSpeechFeedback` hook and sharing `testIds.voiceButton`
                with the `SteerComposer` mic below — two mic controls, one test
                id. The composer's own mic (below) is the sole voice affordance
                now; this guidance stays purely textual. */}
            <p
              className="line-clamp-2 text-center text-[16px] font-semibold leading-tight text-white"
              data-testid={testIds.guidance}
            >
              Pick a look with Foundry
            </p>
            <p className="truncate text-center text-[12px] text-white/52">
              Say the look you want, or ask for a change.
            </p>
            {voice.error && (
              <p
                role="alert"
                data-testid={testIds.voiceError}
                className="rounded-xl bg-red-500/15 px-3 py-2 text-center text-[12px] text-red-200"
              >
                {voice.error}
              </p>
            )}
          </div>
        </div>
        <div
          className="absolute bottom-3 left-3 right-3 grid shrink-0 gap-2"
          data-testid={testIds.composer}
        >
          <SteerComposer
            value={feedback}
            onChange={onFeedbackChange}
            onSubmit={onSubmit}
            disabled={!canCommand || busy}
            micDisabled={!canCommand || !voice.supported || busy}
            placeholder={placeholder}
            textareaAriaLabel="Tell Foundry what to do"
            micTitle={voice.supported ? 'Speak a command' : 'Type a command'}
            micAriaLabel="Voice command"
            sendAriaLabel="Send command"
            listening={voice.listening}
            onMicToggle={voice.listening ? voice.stop : voice.start}
            testIds={{
              micButton: testIds.voiceButton,
              input: testIds.composerInput,
              send: testIds.composerSend,
            }}
          />
        </div>
      </section>
    );
  }

  // ── The #3187 carousel controller (approve-each stages) ───────────────────
  const focusedIndex = cards.findIndex((card) => card.id === focusedAssetId);
  const atFirst = cards.length === 0 || focusedIndex <= 0;
  const atLast = cards.length === 0 || focusedIndex >= cards.length - 1;
  const focusedGenerating = focusedCard !== null && !focusedCard.reviewable;
  const focusedApproved = focusedCard?.status === 'approved';
  const keepItDisabled =
    busy || focusedCard === null || focusedGenerating || focusedApproved || focusedPendingApprove;
  // crucible#2783 — "Saving…" wins over "Tweaking…": while an approve is
  // in-flight/pending both can be true, and the user should see their approve
  // landing, not a regenerate label.
  const keepItLabel = focusedApproved
    ? 'Kept'
    : focusedPendingApprove
      ? 'Saving…'
      : busy
        ? 'Tweaking…'
        : 'Keep it';

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    dragStartX.current = event.clientX;
  };
  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (dragStartX.current === null) return;
    const deltaX = event.clientX - dragStartX.current;
    dragStartX.current = null;
    const step = resolveSwipeStep(deltaX, SWIPE_THRESHOLD_PX);
    if (step !== 0) onStepFocus(step);
  };
  const handlePointerCancel = (): void => {
    dragStartX.current = null;
  };
  // Bugbot LOW (PR #3365 review, "Nav stopPropagation leaves swipe stale") —
  // a gesture that starts on the pad (setting dragStartX) and ends on a nav
  // button never reaches the pad's own onPointerUp (stopPropagation below
  // blocks it), so dragStartX was never cleared. A LATER, unrelated gesture
  // whose pointerdown starts on a button (also intercepted) and whose
  // pointerup lands back on the pad would then compute its delta against
  // that stale coordinate. Clearing on the button's OWN pointerdown — not
  // just stopping propagation — guarantees no stale value survives into the
  // next gesture cycle, regardless of where any given gesture starts or ends.
  const handleNavPointerDown = (event: React.PointerEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    dragStartX.current = null;
  };

  return (
    <section
      className="pointer-events-auto relative flex h-full min-h-0 flex-col gap-2.5 overflow-hidden rounded-[38px] border border-white/15 bg-[#07050c]/95 p-3 shadow-2xl shadow-black/55"
      data-testid={testIds.root}
      data-consumer-surface="phone-driver"
      data-input-primitive="voice-first-text-fallback"
      data-phone-driver-ready="true"
      aria-label="Foundry voice guide"
    >
      {/* "Approve all" — pinned top, always rendered (never hidden) once
          there's an approve-each stage in play. */}
      <button
        type="button"
        onClick={onApproveAll}
        disabled={approveAllBusy || approveAllState !== 'enabled'}
        aria-label="Approve everything left in this stage"
        aria-disabled={approveAllState !== 'enabled'}
        title={
          approveAllState === 'disabled-gate' ? 'Approve your first piece to unlock' : undefined
        }
        data-testid={testIds.approveAll}
        data-approve-all-state={approveAllState}
        className="w-full shrink-0 rounded-2xl border border-white/15 bg-white/8 px-5 py-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-white/85 transition hover:border-crucible-accent/50 hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
      >
        Approve all
        {approveAllState === 'disabled-gate' && (
          <span className="mt-0.5 block text-[10px] font-normal normal-case tracking-normal text-white/45">
            Approve your first piece to unlock
          </span>
        )}
      </button>

      {/* Gesture pad — focused asset's name + position dots. No thumbnail, no
          status chip, no progress line (the TV owns all of that). */}
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded-[28px] border border-white/10 bg-black/45 px-3 py-4"
        data-testid={testIds.pad}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <div className="flex w-full items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => onStepFocus(-1)}
            // crucible#3361 (Bugbot LOW, PR #3353 review) — this button sits
            // INSIDE the pad, which listens for pointerdown/pointerup on
            // itself to resolve swipes. Pointer events bubble, so a press here
            // that drifts past the swipe threshold used to ALSO fire the
            // pad's own swipe-step, double-stepping focus. Stop the pointer
            // events from ever reaching the pad's handlers; onClick (a
            // separate synthetic event) is unaffected.
            onPointerDown={handleNavPointerDown}
            onPointerUp={(event) => event.stopPropagation()}
            disabled={atFirst}
            aria-label="Focus the previous piece"
            data-testid={testIds.prev}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/14 text-xl text-white/70 transition hover:border-white/26 disabled:cursor-not-allowed disabled:opacity-30"
          >
            ‹
          </button>
          <p
            key={focusedAssetId ?? 'none'}
            className="phone-steer-label-fade min-w-0 flex-1 truncate text-center text-[16px] font-semibold text-white"
            data-testid={testIds.focusedLabel}
          >
            {focusedCard
              ? focusedCardLabel(focusedCard)
              : cards.length === 0
                ? 'Foundry is preparing the next piece'
                : 'Loading…'}
          </p>
          <button
            type="button"
            onClick={() => onStepFocus(1)}
            onPointerDown={handleNavPointerDown}
            onPointerUp={(event) => event.stopPropagation()}
            disabled={atLast}
            aria-label="Focus the next piece"
            data-testid={testIds.next}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/14 text-xl text-white/70 transition hover:border-white/26 disabled:cursor-not-allowed disabled:opacity-30"
          >
            ›
          </button>
        </div>
        {cards.length > 0 && (
          <div
            className="flex items-center justify-center gap-1.5"
            data-testid={testIds.dots}
            aria-hidden
          >
            {cards.map((card, index) => (
              <span
                key={card.id}
                className={[
                  'h-1.5 w-1.5 rounded-full transition',
                  index === focusedIndex ? 'bg-crucible-accent' : 'bg-white/25',
                ].join(' ')}
              />
            ))}
          </div>
        )}
        {voice.error && (
          <p
            role="alert"
            data-testid={testIds.voiceError}
            className="rounded-xl bg-red-500/15 px-3 py-2 text-center text-[12px] text-red-200"
          >
            {voice.error}
          </p>
        )}
      </div>

      {/* Steer composer (mic + textarea + send). */}
      <div className="shrink-0" data-testid={testIds.composer}>
        <SteerComposer
          value={feedback}
          onChange={onFeedbackChange}
          onSubmit={onSubmit}
          disabled={!canCommand || busy}
          micDisabled={!canCommand || !voice.supported || busy}
          placeholder={placeholder}
          textareaAriaLabel="Tell Foundry what to do"
          micTitle={voice.supported ? 'Speak a command' : 'Type a command'}
          micAriaLabel="Voice command"
          sendAriaLabel="Send command"
          listening={voice.listening}
          onMicToggle={voice.listening ? voice.stop : voice.start}
          testIds={{
            micButton: testIds.voiceButton,
            input: testIds.composerInput,
            send: testIds.composerSend,
          }}
        />
      </div>

      {/* "Keep it" — full-width brand-gradient CTA, mirrors
          SoulInterviewTurnCard's "Lock this look" treatment exactly. */}
      <button
        type="button"
        onClick={onKeepIt}
        disabled={keepItDisabled}
        data-testid={testIds.keepIt}
        title={focusedGenerating ? 'Still creating — check the TV' : undefined}
        className="glint-halo brand-gradient-fill inline-flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-crucible-accent px-5 py-2.5 text-sm font-semibold text-black shadow-lg shadow-crucible-accent/20 transition hover:brightness-110 disabled:cursor-default disabled:opacity-50"
      >
        {focusedGenerating ? 'Still creating — check the TV' : keepItLabel}
      </button>
    </section>
  );
}

// Re-exported so callers deriving carouselCards()-typed data don't need a
// second import of the same type from carousel-focus.ts.
export type { CarouselCard, StepDirection };
